/**
 * Simple auth gate that verifies the backend session before rendering Studio.
 */
import { ReactNode, useEffect, useState } from 'react'

import { api } from '@/api/client'

const defaultLoginUrl = import.meta.env.VITE_LOGIN_URL || 'https://www.legit2clip.com/login'

type AuthState = 'loading' | 'ready' | 'error'

export function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>('loading')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()

    api
      .get('/auth/check-session', { signal: controller.signal })
      .then((response) => {
        if (cancelled) return
        if (response.data?.authenticated) {
          setState('ready')
        } else {
          window.location.href = `${defaultLoginUrl}?redirect=${encodeURIComponent(window.location.href)}`
        }
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Session check failed', err)
        setError('Unable to verify session. Please refresh or log in again.')
        setState('error')
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [])

  if (state === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-200">
        <div className="space-y-3 text-center">
          <p className="text-sm uppercase tracking-wide text-slate-400">Checking session</p>
          <p className="text-lg font-semibold text-white">Authenticating…</p>
        </div>
      </div>
    )
  }

  if (state === 'error' && error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-200">
        <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-6 text-center">
          <p className="text-sm text-red-100">{error}</p>
          <button
            className="mt-4 rounded-full border border-white/30 px-4 py-2 text-xs uppercase tracking-wide text-white"
            onClick={() => {
              window.location.href = `${defaultLoginUrl}?redirect=${encodeURIComponent(window.location.href)}`
            }}
          >
            Go to login
          </button>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
